import { Link, useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { 
  getArticleBySlug, 
  getAllArticles,
  initializeFearMarketingInteractions,
  initializeHardWorkInteractions,
  InitializeGreatDepressionInteractions,
  InitializeApartmentCompostingInteractions 
} from '../data/articles';
import WageChart from '../components/WageChart.jsx';
import KotterAccordion from '../components/KotterAccordion.jsx'; // 1. Added .jsx extension
import './ArticlePage.css';
import './apartment-composting.css'; 

function ArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = getArticleBySlug(slug);
  const allArticles = getAllArticles();
  
  const [content, setContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const chartRootRef = useRef(null);

  // 1. Scroll to top on every slug change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // 2. Fetch and Sanitize Content
  useEffect(() => {
    if (!article) return;
    setContent(null);
    if (article.contentPath) {
      setContentLoading(true);
      fetch(article.contentPath)
        .then(res => res.text())
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          doc.querySelectorAll('footer, script').forEach(el => el.remove());
          setContent(doc.body.innerHTML);
          setContentLoading(false);
        })
        .catch(() => setContentLoading(false));
    }
  }, [article, slug]);

  // 3. Handle External Injections (Portals)
  useEffect(() => {
    if (content && !contentLoading) {
      // Recharts Injection logic
      if (slug === 'the-impact-of-ford-coolidge-alliance') {
        const container = document.getElementById('recharts-root-ford');
        if (container && !chartRootRef.current) {
          chartRootRef.current = createRoot(container);
          chartRootRef.current.render(<WageChart />);
        }
      }

      // Standard JS Interactions
      if (slug === 'apartment-composting') InitializeApartmentCompostingInteractions();
      if (slug === 'what-is-fear-marketing') initializeFearMarketingInteractions();
      if (slug === 'with-hard-work-and-dedication') initializeHardWorkInteractions();
      if (slug === 'the-great-depression-examining-key-events') InitializeGreatDepressionInteractions();
    }

    return () => {
      if (chartRootRef.current) {
        chartRootRef.current.unmount();
        chartRootRef.current = null;
      }
    };
  }, [slug, content, contentLoading]);

  if (!article) return <div className="container"><h2>Article Not Found</h2></div>;

  const currentIndex = allArticles.findIndex(a => a.slug === slug);
  const prevArticle = allArticles[currentIndex - 1];
  const nextArticle = allArticles[currentIndex + 1];

  const related = allArticles
    .filter(a => a.slug !== slug && a.category === article.category)
    .slice(0, 3);

  return (
    <main className="article-page">
      <div className="container">
        <article className="article">
          <header className="article-header">
            <h1 className="article-title">{article.title}</h1>
            <div className="article-meta">By Cristina Trujillo • {article.date}</div>
          </header>

          {article.image && (
            <figure className="featured-image">
              <img src={article.image} alt={article.title} />
            </figure>
          )}

          <div className="article-content">
            {contentLoading ? (
              <p>Loading...</p>
            ) : (
              <>
                {/* The dynamic HTML content */}
                <div dangerouslySetInnerHTML={{ __html: content }} />

                {/* React Component Injections - Appears at the bottom of the HTML content */}
                {slug === 'how-to-overcome-resistance-to-change' && (
                  <div className="mt-10">
                    <KotterAccordion />
                  </div>
                )}
                
                {/* Note: the Ford WageChart is handled by the ID root inside the HTML */}
              </>
            )}
          </div>

          <nav className="article-nav">
            {prevArticle ? (
              <Link to={`/article/${prevArticle.slug}`} className="nav-btn">← Previous</Link>
            ) : <div />}
            <Link to="/" className="nav-btn home-btn">Return Home</Link>
            {nextArticle ? (
              <Link to={`/article/${nextArticle.slug}`} className="nav-btn">Next →</Link>
            ) : <div />}
          </nav>
        </article>

        <section className="related-articles">
          <h3>Related Articles</h3>
          <div className="related-articles-grid">
            {related.map(r => (
              <Link key={r.slug} to={`/article/${r.slug}`} className="related-card">
                <div className="related-image-wrapper">
                  <img src={r.image || '/placeholder.jpg'} alt={r.title} />
                </div>
                <h4>{r.title}</h4>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}

export default ArticlePage;